export type ParamType =
	| "string"
	| "number"
	| "boolean"
	| "select"
	| "multiselect";

export interface ParamOption {
	value: string;
	label?: string;
}

export interface DeclaredParam {
	key: string;
	label?: string;
	type?: string;
	hint?: string;
	options?: ParamOption[];
	default?: unknown;
}

export const PARAM_TYPES: ParamType[] = [
	"string",
	"number",
	"boolean",
	"select",
	"multiselect",
];

export const PARAM_TYPE_ICONS: Record<ParamType, string> = {
	string: "Type",
	number: "Hash",
	boolean: "ToggleLeft",
	select: "List",
	multiselect: "ListChecks",
};

export const KEY_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export const typeOf = (param: DeclaredParam): ParamType =>
	PARAM_TYPES.includes(param.type as ParamType)
		? (param.type as ParamType)
		: "string";

export const hasOptions = (type: ParamType): boolean =>
	type === "select" || type === "multiselect";

export const suggestKey = (label: string): string => {
	const words = label
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.split(/[^a-zA-Z0-9]+/)
		.filter(Boolean)
		.map((word) => word.toLowerCase());
	if (words.length === 0) return "";
	const joined = words
		.map((word, index) =>
			index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1),
		)
		.join("");
	return /^[0-9]/.test(joined) ? `_${joined}` : joined;
};

export const keyIssue = (
	params: DeclaredParam[],
	index: number,
): string | null => {
	const key = (params[index]?.key ?? "").trim();
	if (key === "") return "A key is required.";
	if (!KEY_PATTERN.test(key))
		return "Letters, digits and underscore only, and not starting with a digit.";
	const clash = params.some(
		(other, i) => i !== index && (other.key ?? "").trim() === key,
	);
	return clash ? "Another parameter already uses this key." : null;
};

export const asList = (value: unknown): string[] => {
	if (value === undefined || value === null || value === "") return [];
	if (Array.isArray(value)) return value.map((entry) => String(entry));
	return String(value)
		.split(",")
		.map((entry) => entry.trim())
		.filter(Boolean);
};

export const defaultIssue = (param: DeclaredParam): string | null => {
	const type = typeOf(param);
	if (!hasOptions(type)) return null;
	if (param.default === undefined || param.default === null) return null;
	const allowed = new Set((param.options ?? []).map((option) => option.value));
	const missing = asList(param.default).filter((entry) => !allowed.has(entry));
	if (missing.length === 0) return null;
	return missing.length === 1
		? `"${missing[0]}" is not one of the options.`
		: `${missing.map((entry) => `"${entry}"`).join(", ")} are not options.`;
};

export const coerceDefault = (type: ParamType, value: unknown): unknown => {
	if (value === undefined || value === null) return undefined;
	if (type === "boolean")
		return typeof value === "boolean" ? value : String(value) === "true";
	if (type === "number") {
		const n = Number(Array.isArray(value) ? value[0] : value);
		return Number.isFinite(n) ? n : undefined;
	}
	if (type === "multiselect") {
		const list = asList(value);
		return list.length > 0 ? list : undefined;
	}
	const single = Array.isArray(value) ? value[0] : value;
	return single === undefined || single === "" ? undefined : String(single);
};

export const jsonText = (value: unknown): string =>
	value === undefined ? "" : JSON.stringify(value);

export const parseJsonText = (
	text: string,
): { ok: true; value: unknown } | { ok: false } => {
	if (text.trim() === "") return { ok: true, value: undefined };
	try {
		return { ok: true, value: JSON.parse(text) as unknown };
	} catch {
		return { ok: false };
	}
};

export const parseOptionList = (text: string): ParamOption[] => {
	const out: ParamOption[] = [];
	const seen = new Set<string>();
	for (const line of text.split(/\r?\n/)) {
		const trimmed = line.trim();
		if (trimmed === "") continue;
		const split = trimmed.indexOf("=");
		const value = (split === -1 ? trimmed : trimmed.slice(0, split)).trim();
		const label = split === -1 ? "" : trimmed.slice(split + 1).trim();
		if (value === "" || seen.has(value)) continue;
		seen.add(value);
		out.push(label === "" || label === value ? { value } : { value, label });
	}
	return out;
};

const displayDefault = (param: DeclaredParam): string | null => {
	const type = typeOf(param);
	if (param.default === undefined || param.default === null) return null;
	if (type === "boolean") return String(param.default === true);
	const list = asList(param.default);
	if (list.length === 0) return null;
	return list.join(", ");
};

export const summariseParam = (param: DeclaredParam): string => {
	const type = typeOf(param);
	const parts: string[] = [];
	if (hasOptions(type)) {
		const count = (param.options ?? []).length;
		parts.push(count === 1 ? "1 option" : `${count} options`);
	}
	const shown = displayDefault(param);
	parts.push(shown === null ? "no default" : `default: ${shown}`);
	return parts.join(" · ");
};

export const optionLabel = (option: ParamOption): string =>
	option.label && option.label.trim() !== "" ? option.label : option.value;
